/**
 * Text input field component.
 * Handles text, email, password, and other input types.
 */

import * as React from "react";
import { Input } from "~/components/ui/input";
import { FieldWrapper, getFieldAriaIds } from "./FieldWrapper";
import type { FieldProps } from "./types";

export function TextField({
  field,
  value,
  touched,
  error,
  label,
  placeholder,
  description,
  extendedDescription,
  onChange,
  onBlur,
}: FieldProps) {
  const hasError = touched && !!error;
  const { descriptionId, extendedDescriptionId, errorId } = getFieldAriaIds(field.name);

  // Build aria-describedby based on available content
  const ariaDescribedByParts: string[] = [];
  if (hasError) {
    ariaDescribedByParts.push(errorId);
  } else {
    if (description) ariaDescribedByParts.push(descriptionId);
    if (extendedDescription) ariaDescribedByParts.push(extendedDescriptionId);
  }
  const ariaDescribedBy = ariaDescribedByParts.length > 0 ? ariaDescribedByParts.join(" ") : undefined;

  return (
    <FieldWrapper
      name={field.name}
      label={label}
      required={field.validation?.required}
      hasError={hasError}
      error={error}
      description={description}
      extendedDescription={extendedDescription}
    >
      <Input
        id={field.name}
        type={field.type}
        value={value as string}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange(field.name, e.target.value)
        }
        onBlur={() => onBlur(field.name)}
        placeholder={placeholder}
        error={hasError ? error : undefined}
        showPasswordToggle={field.showPasswordToggle}
        readOnly={field.readonly}
        disabled={field.readonly}
        aria-describedby={ariaDescribedBy}
        aria-required={field.validation?.required}
        aria-readonly={field.readonly}
        autoComplete="off"
        data-form-type="other"
        data-lpignore="true"
        data-1p-ignore="true"
        className={field.readonly ? "bg-muted cursor-not-allowed" : undefined}
      />
    </FieldWrapper>
  );
}
